
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { NhtsaResponse } from "@/types/vin";

interface VinLookupHistory {
  id: string;
  vin: string;
  created_at: string;
  vehicle_data: NhtsaResponse;
  raw_dataone_response: any;
  api_version: string;
  data_sources: Record<string, boolean>;
}

export function useVinLookup() {
  const [isLoading, setIsLoading] = useState(false);
  const [currentResults, setCurrentResults] = useState<{ nhtsaData: any; dataOneData: any } | null>(null);
  const queryClient = useQueryClient();

  const { data: lookupHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['vinLookups'],
    queryFn: async () => {
      console.log("Fetching VIN lookup history...");
      const { data, error } = await supabase
        .from('vin_lookups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching VIN lookup history:", error);
        throw error;
      }
      
      console.log("VIN lookup history fetched:", data);
      return (data as any[]).map(item => ({
        ...item,
        vehicle_data: item.vehicle_data as unknown as NhtsaResponse,
      })) as VinLookupHistory[];
    },
    staleTime: 30000 // Consider data fresh for 30 seconds
  });

  const lookupVin = async (vin: string) => {
    console.log("Starting VIN lookup for:", vin);
    setIsLoading(true);
    
    try {
      // Check cache first
      const { data: cachedLookup, error: cacheError } = await supabase
        .from('vin_lookups')
        .select('*')
        .eq('vin', vin)
        .maybeSingle();
      
      console.log("Cache lookup result:", cachedLookup, "Error:", cacheError);

      if (cachedLookup) {
        console.log("Using cached VIN data:", cachedLookup);
        const vehicleData = cachedLookup.vehicle_data as unknown as NhtsaResponse;
        const dataOneData = cachedLookup.raw_dataone_response;
        
        const results = {
          nhtsaData: vehicleData.Results[0],
          dataOneData: dataOneData
        };
        
        setCurrentResults(results);
        return results;
      }

      // NHTSA API call with detailed logging
      console.log("Making NHTSA API request for VIN:", vin);
      const nhtsaResponse = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vin}?format=json`);
      const nhtsaData = await nhtsaResponse.json() as NhtsaResponse;
      
      // Log the complete raw NHTSA response
      console.log("Raw NHTSA API Response:", {
        status: nhtsaResponse.status,
        statusText: nhtsaResponse.statusText,
        headers: Object.fromEntries(nhtsaResponse.headers.entries()),
        data: nhtsaData
      });

      // DataOne API call would go here if implemented
      let dataOneData = null;
      // This would be replaced with actual DataOne API integration

      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error("User not authenticated");

      // Store results - now includes null check for dataOneData
      const { data, error } = await supabase
        .from('vin_lookups')
        .insert({
          vin,
          user_id: user.id,
          vehicle_data: nhtsaData as any,
          raw_dataone_response: dataOneData as any,
          data_sources: { 
            nhtsa: true,
            dataone: dataOneData !== null 
          }
        })
        .select()
        .single();

      if (error) throw error;

      // Invalidate and update cache
      queryClient.invalidateQueries({ queryKey: ['vinLookups'] });
      
      const results = {
        nhtsaData: nhtsaData.Results[0],
        dataOneData: dataOneData
      };
      
      setCurrentResults(results);
      return results;
    } catch (error) {
      console.error('VIN lookup error:', error);
      toast.error("Failed to lookup VIN. Please try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearResults = () => {
    setCurrentResults(null);
  };

  const deleteLookup = async (id: string) => {
    try {
      const { error } = await supabase
        .from('vin_lookups')
        .delete()
        .eq('id', id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['vinLookups'] });
      toast.success("VIN lookup deleted successfully");
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Failed to delete VIN lookup");
    }
  };

  return {
    lookupVin,
    lookupHistory: lookupHistory || [],
    isLoading: isLoading || isHistoryLoading,
    deleteLookup,
    currentResults,
    clearResults
  };
}
